import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useRef, useState } from "react";
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import ColumnFilter from "./ColumnFilter";
import moment from "moment";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import CustomFooter from "./Footer";
import { DATE_FORMAT } from "config/const";

const CustomDataGrid = ({
  columns,
  list,
  setItem,
  defaultSort,
  hideFooter,
  hideHeader,
  getRowClassName,
  checkboxSelection,
  disableRowSelectionOnClick,
  onRowSelectionModelChange,
  columnVisibilityModel,
  onColumnOrderChange,
  pageSize,
  pageSizeOptions,
  autoHeight,
  getRowId,
  borderRadius,
  rowHeight,
  minHeight,
  maxHeight,
  hideScroll,
  fileBaseName,
  rowAlign,
  headerAlign,
  headerHeight,
  excelBorder,
  disableColumnSorting,
  selectedCol,
  stickyLeftColumns = []
}) => {
  const theme = useTheme()
  const gridRef = useRef(null);
  const { bt } = useBizTypeTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const paletteMode = useSelector((state) => state?.userSettings?.paletteMode);
  const [sortModel, setSortModel] = useState(defaultSort ?? []);
  const [columnsList, setColumnsList] = useState([]);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [filters, setFilters] = useState({});
  const [isStickyActive, setIsStickyActive] = useState(false);

  useEffect(() => {
    if (columns?.length > 0) {
      const listFiltered = columns?.filter((column) => !column?.hide);

      const withFilters = listFiltered?.map((col, index) => ({
        ...col,
        width: stickyLeftColumns.includes(col.field)
          ? isStickyActive
            ? col.width / 1.5
            : col.width
          : col.width,

        minWidth: stickyLeftColumns.includes(col.field)
          ? isStickyActive
            ? col.minWidth / 2
            : col.minWidth
          : col.minWidth,

        headerClassName: stickyLeftColumns.includes(col.field)
          ? `sticky-left sticky-left-${stickyLeftColumns.indexOf(col.field)}`
          : col.headerClassName,

        cellClassName: stickyLeftColumns.includes(col.field)
          ? `sticky-left sticky-left-${stickyLeftColumns.indexOf(col.field)}`
          : col.cellClassName,

        renderHeader: (params) => {
          return (
            <Stack direction="row" alignItems="center">
              {params?.colDef?.filterable ? (
                <ColumnFilter
                  column={col}
                  rows={list}
                  bt={bt}
                  selectedValues={filters[col.field] || []}
                  onFilterChange={handleFilterChange}
                  isValueDate={isValueDate}
                  normalizeValue={normalizeValue}
                  displayValue={displayValue}
                />
              ) : null}

              <Typography sx={{ p: 0 }} variant="body2" fontWeight="bold">
                {col.headerName}
              </Typography>
            </Stack>
          );
        },
      }));

      setColumnsList(withFilters);
    }
  }, [columns, filters, list, isStickyActive]);

  useEffect(() => {
    const scroller = gridRef.current?.querySelector(".MuiDataGrid-virtualScroller");

    if (!scroller) return;

    const handleScroll = () => {
      setIsStickyActive(scroller.scrollLeft < -80);
    };

    scroller.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => scroller.removeEventListener("scroll", handleScroll);
  }, []);

  const handleFilterChange = (field, values) => {
    setFilters((prev) => ({ ...prev, [field]: values }));
  };

  const isValueDate = (value) => {
    const date = moment(value, moment.ISO_8601, true);
    return date.isValid() && typeof value === "string" && value.length >= 8;
  };

  const normalizeValue = (value) => {
    return isValueDate(value)
      ? moment(value).format("YYYY-MM-DD")
      : value;
  };

  const displayValue = (value) => {
    return isValueDate(value)
      ? moment(value).format(DATE_FORMAT)
      : value === true
        ? bt('YES')
        : value === false
          ? bt('NO')
          : bt(String(value));
  };

  const filteredRows = list?.filter((row) =>
    Object.entries(filters).every(([field, values]) => {
      if (values.length === 0) return true;

      const cellValue = row[field];
      const isDateField = values.some((val) =>
        moment(val, moment.ISO_8601, true).isValid()
      );

      if (isDateField) {
        return values.some((val) =>
          moment(cellValue).isSame(moment(val), "day")
        );
      }

      return values.includes(cellValue);
    })
  );

  const onRowClick = (e) => {
    if (e?.row?.disabled) return;
    if (setItem) setItem(e?.row);
  };

  return (
    <DataGrid
      ref={gridRef}
      disableVirtualization
      getRowId={getRowId}
      localeText={{ noRowsLabel: bt("NO_DATA") }}
      getRowClassName={getRowClassName}
      hideFooter={hideFooter ?? false}
      disableColumnSorting={disableColumnSorting ?? false}
      columnVisibilityModel={columnVisibilityModel}
      onColumnOrderChange={onColumnOrderChange}
      initialState={{ pagination: { paginationModel: { pageSize: pageSize ?? 100 } } }}
      sortModel={sortModel}
      onColumnHeaderClick={(params) =>
        selectedCol ? setSelectedColumn(prev => prev === params.field ? null : params.field) : null
      }
      slots={{ footer: CustomFooter }}
      slotProps={{
        footer: {
          fileBaseName: fileBaseName || "mybee-file",
          isValueDate,
          normalizeValue,
          displayValue,
          columnsList,
        }
      }}
      onSortModelChange={(model) => setSortModel(model)}
      onRowClick={onRowClick}
      hideFooterSelectedRowCount
      disableRowSelectionOnClick={disableRowSelectionOnClick ?? false}
      pageSizeOptions={pageSizeOptions ?? [100]}
      rowReordering
      checkboxSelection={checkboxSelection}
      onRowSelectionModelChange={onRowSelectionModelChange}
      disableColumnMenu
      autoHeight={autoHeight ?? false}
      columns={columnsList}
      rows={filteredRows}
      isRowSelectable={(params) => !params.row.disabled}
      rowHeight={rowHeight ? rowHeight : 52}
      columnHeaderHeight={headerHeight ? headerHeight : 52}
      showCellVerticalBorder={excelBorder ? true : false}
      showColumnVerticalBorder={excelBorder ? true : false}

      sx={{
        minHeight: minHeight ? minHeight : 150,
        maxHeight: maxHeight ? maxHeight : "auto",
        width: '100%',
        bgcolor: 'bg.main',
        border: 0,
        borderRadius: borderRadius ? borderRadius : `${isMobile ? "15px 15px 0px 0px" : "20px 20px 0px 0px"}`,
        boxShadow: paletteMode === "dark" ? "0px 0px 2px 1px #44444480" : "0px 0px 10px 1px #EEEEEE80",
        ...(hideHeader && {
          "& .MuiDataGrid-columnHeaders": {
            height: 0,
            minHeight: 0,
            maxHeight: 0,
            borderBottom: "none",
            overflow: "hidden",
          },
          "& .MuiDataGrid-columnHeader": {
            padding: 0,
            margin: 0,
            visibility: "hidden", // משאיר measure אבל לא מציג
          },
          "& .MuiDataGrid-iconSeparator": { display: "none" },
        }),

        ...(selectedColumn && {
          [`& .MuiDataGrid-columnHeader[data-field="${selectedColumn}"]`]: {
            backgroundColor: "rgba(25,118,210,0.15)",
          },

          [`& .MuiDataGrid-cell[data-field="${selectedColumn}"]`]: {
            backgroundColor: "rgba(25,118,210,0.08)",
          },
        }),

        border: `${excelBorder ? "1px" : "0px"} solid #e0e0e0`,
        "& .MuiDataGrid-columnHeaderTitleContainer": {
          justifyContent: headerAlign ? headerAlign : "start",
        },
        "& .MuiDataGrid-cell": {
          display: "flex",
          justifyContent: rowAlign ? rowAlign : "start",
          alignItems: "center",
        },
        "& .selected-column": {
          backgroundColor: "rgba(158, 199, 239, 0.1)",
        },
        "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
          outline: "none",
        },
        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
          outline: "none",
        },
        "& .MuiDataGrid-row:focus, & .MuiDataGrid-row.Mui-selected:focus": {
          outline: "none",
        },
        "& .MuiDataGrid-row": {
          transition: "background-color 0.2s ease, transform 0.2s ease",
        },
        "& .MuiDataGrid-row:active": {
          transform: "scale(0.99)",
        },
        "& .MuiDataGrid-row.hot": {
          backgroundColor: paletteMode === "dark" ? "#662222" : "#ffcccc",
        },
        "& .MuiDataGrid-row.cold": {
          backgroundColor: paletteMode === "dark" ? "#1f3b25" : "#adebb3",
        },
        "& .MuiDataGrid-row:hover": {
          backgroundColor: paletteMode === "dark" ? "#2a2a2a !important" : "#f7f7f7 !important",
          cursor: "pointer",
        },
        "& .MuiDataGrid-row.hot:hover": {
          backgroundColor: paletteMode === "dark" ? "#8b2e2e !important" : "#fddada !important",
        },
        "& .MuiDataGrid-row.cold:hover": {
          backgroundColor: paletteMode === "dark" ? "#2e5a38 !important" : "#c1f1c5 !important",
        },
        "& .MuiDataGrid-row.Mui-selected": {
          backgroundColor: paletteMode === "dark" ? "#3a3a3a !important" : "#ececec !important",
          fontWeight: "bold",
        },
        "& .MuiDataGrid-row.hot.Mui-selected": {
          backgroundColor: paletteMode === "dark" ? "#a13b3b !important" : "#fa7171 !important",
        },
        "& .MuiDataGrid-row.cold.Mui-selected": {
          backgroundColor: paletteMode === "dark" ? "#3ba155 !important" : "#79e185 !important",
        },
        "& *": {
          scrollbarWidth: hideScroll ? "none" : "auto",
          msOverflowStyle: hideScroll ? "none" : "auto",
        },

        "& .sticky-left": {
          position: "sticky",
          zIndex: 999,
          backgroundColor: "background.paper",
          transition: "font-size 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease",
          ...(isStickyActive && {
            fontSize: "0.6rem",
            boxShadow: "2px 0 6px rgba(0,0,0,0.12)",
          }),
        },

        "& .MuiDataGrid-columnHeader.sticky-left": {
          zIndex: 1000,
          backgroundColor: "background.paper",
          ...(isStickyActive && {
            fontSize: "0.6rem",
          }),
        },

        "& .sticky-left-0": {
          left: 0,
        },

        "& .sticky-left-1": {
          left: 80,
        },

        "& .sticky-left-2": {
          left: 80,
        },
        '--DataGrid-overlayHeight': '60px',
        // '& .MuiDataGrid-overlay': {
        //   height: 30,
        // },
        // '& .MuiDataGrid-overlayWrapper': {
        //   height: 30,
        // },
      }}
    />
  );
};

export default CustomDataGrid;
